import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Activity,
    AlertCircle,
    CalendarDays,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    ClipboardList,
    Clock3,
    Filter,
    Loader2,
    Pencil,
    Plus,
    Search,
    Trash2,
    Users,
    X,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import ConfirmModal from "../../components/ui/ConfirmModal";

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const emptyScheduledTasks = () =>
    Array.from({ length: 31 }, () => ({
        title: "",
        description: "",
    }));

const buildWeeklyDates = (startDate) => {
    if (!startDate) return [];
    const [year, month, day] = String(startDate).split("-").map(Number);
    if (!year || !month || !day) return [];
    const start = new Date(year, month - 1, day);
    return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return {
            key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
            label: date.toLocaleDateString("en-IN", {
                weekday: "short",
                day: "2-digit",
                month: "short",
            }),
            isSunday: date.getDay() === 0,
        };
    });
};

const buildRangeDates = (fromDate, toDate) => {
    if (!fromDate || !toDate) return [];
    const [startYear, startMonth, startDay] = String(fromDate).split("-").map(Number);
    const [endYear, endMonth, endDay] = String(toDate).split("-").map(Number);
    if (!startYear || !startMonth || !startDay || !endYear || !endMonth || !endDay) return [];
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    if (end < start) return [];

    const dates = [];
    for (const date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        dates.push({
            key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
            label: date.toLocaleDateString("en-IN", {
                weekday: "short",
                day: "2-digit",
                month: "short",
            }),
            isSunday: date.getDay() === 0,
        });
    }
    return dates;
};

const getTaskDateKey = (task) => {
    if (task?.scheduleDate) return String(task.scheduleDate).slice(0, 10);
    if (!task?.dueDate) return "No due date";
    return String(task.dueDate).slice(0, 10);
};

const getTaskAssignees = (task) => {
    const assigned = Array.isArray(task?.assignedTo) ? task.assignedTo : [task?.assignedTo];
    return assigned
        .filter(Boolean)
        .map((employee, index) =>
            typeof employee === "string"
                ? { id: employee, name: employee, role: "", team: task?.team || "" }
                : {
                      id: employee?._id || employee?.id || `${employee?.name || "employee"}-${index}`,
                      name: employee?.name || "Unassigned employee",
                      role: employee?.role || "",
                      team: employee?.team || task?.team || "",
                  }
        );
};

const getTaskAssignerName = (task) => {
    if (task?.assignedBy && typeof task.assignedBy === "object") {
        return task.assignedBy.name || task.assignedBy.email || "Super Admin";
    }
    return task?.assignedByName || "Super Admin";
};

const formatTaskDateHeading = (dateKey) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) return dateKey;
    const [year, month, day] = String(dateKey).split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const priorityStyles = (p) => {
    const priority = String(p || "").toLowerCase();
    if (priority === "high") {
        return "bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/20";
    }
    if (priority === "medium") {
        return "bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20";
    }
    return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20";
};

const statusBadgeStyles = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed") {
        return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
    }
    if (s === "in-progress") {
        return "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30";
    }
    if (s === "blocked") {
        return "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30";
    }
    return "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30";
};

const Tasks = () => {
    const navigate = useNavigate();

    const [tasks, setTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [taskTeam, setTaskTeam] = useState("");
    const [assignToEntireTeam, setAssignToEntireTeam] = useState(false);

    const [teams, setTeams] = useState([
        { _id: "software", name: "Software", value: "software" },
        { _id: "embedded", name: "Embedded", value: "embedded" },
        { _id: "designteam", name: "Design Team", value: "designteam" },
        { _id: "sales team", name: "Sales Team", value: "sales team" },
        { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
        { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" },
    ]);

    const [search, setSearch] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // Modal state for viewing an employee's tasks
    const [selectedEmployeeKey, setSelectedEmployeeKey] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [editingTask, setEditingTask] = useState(null);
    const [editForm, setEditForm] = useState({
        title: "",
        description: "",
        priority: "medium",
        dueDate: "",
        status: "pending",
    });
    const [savingEdit, setSavingEdit] = useState(false);

    const [form, setForm] = useState({
        title: "",
        description: "",
        priority: "medium",
        assignmentMode: "single",
        weeklyWorkMode: "same",
        weeklyTasks: emptyScheduledTasks(),
        dueDate: "",
        fromDate: "",
        toDate: "",
    });

    const today = new Date().toISOString().split("T")[0];

    // ================= FETCH TASKS =================
    const fetchTasks = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/tasks");
            setTasks(res.data.tasks || []);
        } catch (err) {
            console.error("Failed to fetch tasks:", err);
            if ([401, 403].includes(err.response?.status)) {
                navigate("/admin/login");
            }
        } finally {
            setInitialLoading(false);
        }
    }, [navigate]);

    // ================= FETCH EMPLOYEES & TEAMS =================
    const fetchEmployees = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/employees/all");
            if (Array.isArray(res.data)) {
                setEmployees(res.data);
            } else {
                setEmployees([]);
            }
        } catch (err) {
            console.error("Failed to fetch employees:", err);
        }
    }, []);

    const fetchTeamsList = useCallback(async () => {
        try {
            const res = await api.get("/api/teams");
            if (res.data && res.data.length > 0) {
                const dbTeams = res.data.map((t) => ({
                    _id: t._id,
                    name: t.name,
                    value: t.name,
                }));
                const defaults = [
                    { _id: "software", name: "Software", value: "software" },
                    { _id: "embedded", name: "Embedded", value: "embedded" },
                    { _id: "designteam", name: "Design Team", value: "designteam" },
                    { _id: "sales team", name: "Sales Team", value: "sales team" },
                    { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
                    { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" },
                ];
                const merged = [...dbTeams];
                defaults.forEach((def) => {
                    if (!merged.some((t) => t.name.toLowerCase() === def.name.toLowerCase())) {
                        merged.push(def);
                    }
                });
                setTeams(merged);
            }
        } catch (err) {
            console.error("Failed to fetch teams:", err);
        }
    }, []);

    useEffect(() => {
        document.title = "Tasks | Savrion Technologies";
        fetchTasks();
        fetchEmployees();
        fetchTeamsList();
    }, [fetchTasks, fetchEmployees, fetchTeamsList]);

    // Background auto-refresh (silent without UI flashing)
    useEffect(() => {
        const refreshTasksSilently = () => {
            if (!document.hidden) {
                fetchTasks();
            }
        };

        window.addEventListener("focus", refreshTasksSilently);
        document.addEventListener("visibilitychange", refreshTasksSilently);
        const refreshId = window.setInterval(refreshTasksSilently, 20000);

        return () => {
            window.removeEventListener("focus", refreshTasksSilently);
            document.removeEventListener("visibilitychange", refreshTasksSilently);
            window.clearInterval(refreshId);
        };
    }, [fetchTasks]);

    // ================= FILTER EMPLOYEES FOR CREATE MODAL =================
    const filteredEmployeesForTeam = useMemo(() => {
        if (!taskTeam) return [];
        return employees.filter(
            (e) => (e.team || "").toLowerCase() === taskTeam.toLowerCase()
        );
    }, [employees, taskTeam]);

    // ================= FILTER TASKS =================
    const filteredTasks = useMemo(() => {
        const q = search.trim().toLowerCase();

        return tasks.filter((t) => {
            const matchesDate = !dateFilter || getTaskDateKey(t) === dateFilter;
            const matchesStatus =
                statusFilter === "all" || (t.status || "").toLowerCase() === statusFilter.toLowerCase();

            if (!matchesDate || !matchesStatus) return false;
            if (!q) return true;

            const assigneeNames = getTaskAssignees(t)
                .map((emp) => emp.name)
                .join(" ")
                .toLowerCase();

            return (
                (t.title || "").toLowerCase().includes(q) ||
                (t.description || "").toLowerCase().includes(q) ||
                (t.team || "").toLowerCase().includes(q) ||
                assigneeNames.includes(q)
            );
        });
    }, [tasks, search, dateFilter, statusFilter]);

    const availableTaskDates = useMemo(() => {
        return [...new Set(tasks.map(getTaskDateKey))]
            .filter((dateKey) => /^\d{4}-\d{2}-\d{2}$/.test(dateKey))
            .sort((a, b) => b.localeCompare(a));
    }, [tasks]);

    // ================= GROUP TASKS BY EMPLOYEE =================
    const employeeWiseTasks = useMemo(() => {
        const groups = new Map();

        filteredTasks.forEach((task) => {
            const assignees = getTaskAssignees(task);
            const normalizedAssignees = assignees.length
                ? assignees
                : [{ id: "unassigned", name: "Unassigned", role: "", team: task?.team || "" }];

            normalizedAssignees.forEach((employee) => {
                const key = String(employee.id || employee.name);
                if (!groups.has(key)) {
                    groups.set(key, { employee, tasks: [] });
                }
                groups.get(key).tasks.push(task);
            });
        });

        return [...groups.values()]
            .map((group) => ({
                ...group,
                tasks: group.tasks.sort(
                    (a, b) =>
                        getTaskDateKey(a).localeCompare(getTaskDateKey(b)) ||
                        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                ),
            }))
            .sort((a, b) => a.employee.name.localeCompare(b.employee.name));
    }, [filteredTasks]);

    // Active Employee Group for Modal Popup
    const activeEmployeeGroup = useMemo(() => {
        if (!selectedEmployeeKey) return null;
        return (
            employeeWiseTasks.find(
                ({ employee }) => String(employee.id || employee.name) === selectedEmployeeKey
            ) || null
        );
    }, [selectedEmployeeKey, employeeWiseTasks]);

    // Reset pagination on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, dateFilter, statusFilter, itemsPerPage]);

    // Paginate grouped employees
    const totalPages = useMemo(() => {
        if (itemsPerPage === "All") return 1;
        return Math.max(1, Math.ceil(employeeWiseTasks.length / itemsPerPage));
    }, [employeeWiseTasks.length, itemsPerPage]);

    const paginatedEmployeeGroups = useMemo(() => {
        if (itemsPerPage === "All") return employeeWiseTasks;
        const start = (currentPage - 1) * itemsPerPage;
        return employeeWiseTasks.slice(start, start + itemsPerPage);
    }, [currentPage, employeeWiseTasks, itemsPerPage]);

    // Dates for Create Modal
    const weeklyDates = useMemo(
        () =>
            form.assignmentMode === "range"
                ? buildRangeDates(form.fromDate, form.toDate)
                : buildWeeklyDates(form.dueDate),
        [form.assignmentMode, form.dueDate, form.fromDate, form.toDate]
    );

    // ================= STATS =================
    const stats = useMemo(() => {
        const total = tasks.length;
        const pending = tasks.filter((t) => t.status === "pending").length;
        const progress = tasks.filter((t) => t.status === "in-progress").length;
        const blocked = tasks.filter((t) => t.status === "blocked").length;
        const completed = tasks.filter((t) => t.status === "completed").length;

        return {
            total,
            pending,
            progress,
            blocked,
            completed,
        };
    }, [tasks]);

    // ================= CHECKBOX FOR EMPLOYEE SELECTION =================
    const handleCheckbox = (id) => {
        setSelectedEmployees((prev) =>
            prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
        );
    };

    // ================= CREATE TASK =================
    const handleCreate = async (e) => {
        e.preventDefault();

        try {
            if (!taskTeam) {
                return alert("Please select a team first");
            }

            if (!assignToEntireTeam && !selectedEmployees.length) {
                return alert("Select at least one employee or choose entire team");
            }

            if (form.assignmentMode === "range") {
                if (!form.fromDate || !form.toDate) {
                    return alert("Please select from date and to date");
                }
                if (form.toDate < form.fromDate) {
                    return alert("To date should be after from date");
                }
                if (!weeklyDates.length) {
                    return alert("Selected range has no dates");
                }
            } else if (!form.dueDate) {
                return alert("Please select a task date");
            }

            const isDayWiseScheduledWork =
                form.assignmentMode === "range" ||
                (form.assignmentMode === "weekly" && form.weeklyWorkMode === "different");

            if (isDayWiseScheduledWork) {
                const missingDay = weeklyDates.some((day, index) => {
                    const item = form.weeklyTasks[index] || {};
                    return !day.key || !item.title.trim() || !item.description.trim();
                });
                if (missingDay) {
                    return alert("Fill task title and description for every scheduled day");
                }
            } else if (!form.title.trim() || !form.description.trim()) {
                return alert("Task title and description are required");
            }

            const payload = {
                ...form,
                dueDate: form.assignmentMode === "range" ? form.fromDate : form.dueDate,
                weeklyWorkMode: form.assignmentMode === "range" ? "different" : form.weeklyWorkMode,
                weeklyTasks: isDayWiseScheduledWork
                    ? weeklyDates.map((day, index) => ({
                          date: day.key,
                          title: form.weeklyTasks[index]?.title || "",
                          description: form.weeklyTasks[index]?.description || "",
                      }))
                    : [],
                team: taskTeam,
                assignToEntireTeam,
                assignedTo: assignToEntireTeam ? [] : selectedEmployees,
            };

            const createResponse = await api.post("/api/admin/tasks", payload);

            alert(
                createResponse.data?.emailDelivery?.failed
                    ? "Task created, but one or more assignment emails could not be delivered."
                    : "Task created and assignment email sent successfully"
            );

            setForm({
                title: "",
                description: "",
                priority: "medium",
                assignmentMode: "single",
                weeklyWorkMode: "same",
                weeklyTasks: emptyScheduledTasks(),
                dueDate: "",
                fromDate: "",
                toDate: "",
            });

            setSelectedEmployees([]);
            setAssignToEntireTeam(false);
            setTaskTeam("");
            setShowCreateForm(false);

            fetchTasks();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Task creation failed");
        }
    };

    // ================= STATUS CHANGE =================
    const handleStatusChange = async (id, newStatus) => {
        if (updatingId === id) return;

        // Optimistic UI update
        setTasks((prev) =>
            prev.map((t) => (t._id === id ? { ...t, status: newStatus } : t))
        );

        try {
            setUpdatingId(id);
            await api.patch(`/api/admin/tasks/${id}/status`, {
                status: newStatus,
            });
        } catch (err) {
            console.error("Status update failed:", err);
            alert(err.response?.data?.message || "Failed to update status");
            fetchTasks();
        } finally {
            setUpdatingId(null);
        }
    };

    // ================= EDIT =================
    const openEdit = (task) => {
        setEditingTask(task);
        setEditForm({
            title: task?.title || "",
            description: task?.description || "",
            priority: task?.priority || "medium",
            dueDate: getTaskDateKey(task) === "No due date" ? "" : getTaskDateKey(task),
            status: task?.status || "pending",
        });
    };

    const closeEdit = () => {
        setEditingTask(null);
        setEditForm({
            title: "",
            description: "",
            priority: "medium",
            dueDate: "",
            status: "pending",
        });
    };

    const handleEditSave = async (event) => {
        event.preventDefault();
        if (!editingTask?._id) return;
        if (!editForm.title.trim()) {
            return alert("Task title is required");
        }
        if (!editForm.dueDate) {
            return alert("Due date is required");
        }

        try {
            setSavingEdit(true);
            const res = await api.patch(`/api/admin/tasks/${editingTask._id}`, {
                title: editForm.title,
                description: editForm.description,
                priority: editForm.priority,
                dueDate: editForm.dueDate,
                status: editForm.status,
            });

            setTasks((prev) =>
                prev.map((task) =>
                    task._id === editingTask._id ? { ...task, ...res.data } : task
                )
            );
            closeEdit();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Task update failed");
        } finally {
            setSavingEdit(false);
        }
    };

    // ================= DELETE =================
    const openDelete = (task) => {
        setSelectedTask(task);
        setConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedTask?._id) return;
        try {
            setDeleting(true);
            await api.delete(`/api/admin/tasks/${selectedTask._id}`);
            setTasks((prev) => prev.filter((t) => t._id !== selectedTask._id));
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Delete failed");
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
            setSelectedTask(null);
        }
    };

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] transition-colors duration-200">
            <AdminNavbar />

            <main className="flex-1 min-w-0 px-4 md:px-7 py-6 space-y-6">
                {/* ================= HERO BANNER ================= */}
                <div className="relative overflow-hidden rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 sm:p-7 shadow-[var(--shadow-soft)]">
                    <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] text-xs font-bold mb-3">
                                <Activity size={15} />
                                Task Workflow Center
                            </div>

                            <h1 className="text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">
                                Task Management
                            </h1>

                            <p className="mt-1 text-sm text-[var(--text-secondary)]">
                                Create, assign, monitor, and update employee workload effortlessly.
                            </p>

                            <div className="mt-4 flex flex-wrap items-center gap-2.5">
                                <span className="rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)] px-3.5 py-1 text-xs font-bold text-[var(--text-primary)]">
                                    {stats.total} Total Tasks
                                </span>
                                <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-3.5 py-1 text-xs font-bold text-amber-600 dark:text-amber-400">
                                    {stats.pending} Pending
                                </span>
                                <span className="rounded-full border border-cyan-500/20 bg-cyan-500/10 px-3.5 py-1 text-xs font-bold text-cyan-600 dark:text-cyan-400">
                                    {stats.progress} In Progress
                                </span>
                                <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3.5 py-1 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                    {stats.completed} Completed
                                </span>
                            </div>
                        </div>

                        <div>
                            <button
                                type="button"
                                onClick={() => setShowCreateForm(true)}
                                className="inline-flex h-12 items-center gap-2 rounded-2xl bg-[var(--accent-primary)] px-6 font-bold text-white shadow-md shadow-[var(--accent-primary)]/20 transition-all hover:bg-[var(--accent-hover)]"
                            >
                                <Plus size={18} />
                                Create Task
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= STATS CARDS ================= */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {[
                        {
                            title: "Total Tasks",
                            value: stats.total,
                            icon: <ClipboardList size={22} />,
                            color: "text-[var(--accent-primary)] bg-[var(--accent-primary)]/10",
                        },
                        {
                            title: "Pending Tasks",
                            value: stats.pending,
                            icon: <AlertCircle size={22} />,
                            color: "text-amber-500 bg-amber-500/10",
                        },
                        {
                            title: "In Progress",
                            value: stats.progress,
                            icon: <Clock3 size={22} />,
                            color: "text-cyan-500 bg-cyan-500/10",
                        },
                        {
                            title: "Completed",
                            value: stats.completed,
                            icon: <CheckCircle2 size={22} />,
                            color: "text-emerald-500 bg-emerald-500/10",
                        },
                    ].map((item, idx) => (
                        <div
                            key={idx}
                            className="rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-[var(--shadow-soft)] transition-all hover:shadow-[var(--shadow-matte)]"
                        >
                            <div className="flex items-center justify-between">
                                <div
                                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${item.color}`}
                                >
                                    {item.icon}
                                </div>
                                <span className="text-3xl font-black text-[var(--text-primary)]">
                                    {item.value}
                                </span>
                            </div>
                            <h3 className="mt-4 text-sm font-semibold text-[var(--text-secondary)]">
                                {item.title}
                            </h3>
                        </div>
                    ))}
                </div>

                {/* ================= FILTERS & CONTROLS ================= */}
                <div className="rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-center">
                        {/* SEARCH */}
                        <div className="relative">
                            <Search
                                size={18}
                                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                            />
                            <input
                                type="text"
                                placeholder="Search by title, description, or employee..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="h-11 w-full rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-4 text-sm font-medium outline-none transition-all focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/15"
                            />
                        </div>

                        {/* STATUS FILTER */}
                        <div className="relative">
                            <Filter
                                size={17}
                                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                            />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="h-11 w-full appearance-none rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-8 text-sm font-semibold outline-none transition-all focus:border-[var(--accent-primary)]"
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="in-progress">In Progress</option>
                                <option value="blocked">Blocked</option>
                                <option value="completed">Completed</option>
                            </select>
                            <ChevronDown
                                size={16}
                                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                            />
                        </div>

                        {/* DATE FILTER */}
                        <div className="relative">
                            <CalendarDays
                                size={17}
                                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                            />
                            <select
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="h-11 w-full appearance-none rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-8 text-sm font-semibold outline-none transition-all focus:border-[var(--accent-primary)]"
                            >
                                <option value="">All task dates</option>
                                {availableTaskDates.map((dateKey) => (
                                    <option key={dateKey} value={dateKey}>
                                        {formatTaskDateHeading(dateKey)}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown
                                size={16}
                                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                            />
                        </div>
                    </div>

                    {(search || dateFilter || statusFilter !== "all") && (
                        <div className="mt-3 flex items-center justify-between border-t border-[var(--border-color)] pt-3 text-xs font-bold text-[var(--text-secondary)]">
                            <span>Filtering active</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch("");
                                    setDateFilter("");
                                    setStatusFilter("all");
                                }}
                                className="text-[var(--accent-primary)] hover:underline"
                            >
                                Clear all filters
                            </button>
                        </div>
                    )}
                </div>

                {/* ================= EMPLOYEE WORKLOAD SECTION ================= */}
                <section className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                        <div>
                            <p className="text-xs font-black uppercase tracking-wider text-[var(--accent-primary)]">
                                Workload Breakdown
                            </p>
                            <h2 className="text-xl font-black text-[var(--text-primary)]">
                                Tasks Grouped by Employee
                            </h2>
                            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                Click on any employee card to view their tasks in a pop-up window
                            </p>
                        </div>

                        <span className="rounded-full border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-1.5 text-xs font-bold text-[var(--text-secondary)]">
                            {employeeWiseTasks.length} Employee{employeeWiseTasks.length === 1 ? "" : "s"} •{" "}
                            {filteredTasks.length} Task{filteredTasks.length === 1 ? "" : "s"}
                        </span>
                    </div>

                    {/* INITIAL LOADING STATE */}
                    {initialLoading ? (
                        <div className="flex flex-col items-center justify-center rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] py-20 text-center">
                            <Loader2 size={36} className="animate-spin text-[var(--accent-primary)] mb-3" />
                            <p className="text-base font-bold text-[var(--text-primary)]">
                                Loading assigned tasks...
                            </p>
                            <p className="text-xs text-[var(--text-secondary)] mt-1">
                                Fetching live employee workloads
                            </p>
                        </div>
                    ) : paginatedEmployeeGroups.length === 0 ? (
                        /* EMPTY STATE */
                        <div className="rounded-3xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] px-6 py-16 text-center shadow-[var(--shadow-soft)]">
                            <ClipboardList className="mx-auto mb-3 text-[var(--text-secondary)]" size={36} />
                            <h3 className="text-lg font-black text-[var(--text-primary)]">
                                No matching tasks found
                            </h3>
                            <p className="mt-1 text-sm text-[var(--text-secondary)]">
                                Try changing your search query, status filter, or task date.
                            </p>
                        </div>
                    ) : (
                        /* EMPLOYEE CARDS (CLICK TO OPEN POPUP MODAL) */
                        paginatedEmployeeGroups.map(({ employee, tasks: assignedTasks }) => {
                            const employeeKey = String(employee.id || employee.name);
                            const completedCount = assignedTasks.filter((t) => t.status === "completed").length;
                            const progressCount = assignedTasks.filter((t) => t.status === "in-progress").length;
                            const initials = (employee.name || "E")
                                .split(/\s+/)
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((part) => part.charAt(0).toUpperCase())
                                .join("");

                            return (
                                <article
                                    key={employeeKey}
                                    onClick={() => setSelectedEmployeeKey(employeeKey)}
                                    className="cursor-pointer overflow-hidden rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 sm:px-6 sm:py-4.5 shadow-[var(--shadow-soft)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--accent-primary)]/40 hover:shadow-[var(--shadow-matte)]"
                                >
                                    <div className="flex flex-wrap items-center justify-between gap-4">
                                        <div className="flex min-w-0 items-center gap-3.5">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-primary)]/10 text-sm font-black text-[var(--accent-primary)]">
                                                {initials || "--"}
                                            </div>

                                            <div className="min-w-0">
                                                <h3 className="truncate text-base font-black text-[var(--text-primary)]">
                                                    {employee.name}
                                                </h3>
                                                <p className="truncate text-xs font-semibold text-[var(--text-secondary)]">
                                                    {[formatRoleLabel(employee.role), employee.team]
                                                        .filter(Boolean)
                                                        .join(" • ") || "Employee"}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 text-xs font-bold">
                                            {/* COUNTER BADGES */}
                                            <span className="rounded-xl bg-[var(--accent-primary)]/10 px-3 py-1.5 text-[var(--accent-primary)]">
                                                {assignedTasks.length} Assigned
                                            </span>

                                            {completedCount > 0 && (
                                                <span className="hidden sm:inline-flex rounded-xl bg-emerald-500/10 px-3 py-1.5 text-emerald-600 dark:text-emerald-400">
                                                    {completedCount} Done
                                                </span>
                                            )}

                                            {progressCount > 0 && (
                                                <span className="hidden md:inline-flex rounded-xl bg-cyan-500/10 px-3 py-1.5 text-cyan-600 dark:text-cyan-400">
                                                    {progressCount} Active
                                                </span>
                                            )}

                                            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] group-hover:text-[var(--accent-primary)]">
                                                <ChevronRight size={18} />
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </section>

                {/* ================= PAGINATION ================= */}
                {!initialLoading && employeeWiseTasks.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                        <div className="flex items-center gap-2.5 text-xs font-bold text-[var(--text-secondary)]">
                            <span>Show:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) =>
                                    setItemsPerPage(
                                        e.target.value === "All" ? "All" : Number(e.target.value)
                                    )
                                }
                                className="h-9 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 text-xs font-bold text-[var(--text-primary)] outline-none focus:border-[var(--accent-primary)]"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value="All">All</option>
                            </select>
                            <span>
                                {itemsPerPage === "All"
                                    ? `Showing all ${employeeWiseTasks.length} employees`
                                    : `Showing ${Math.min(
                                          (currentPage - 1) * itemsPerPage + 1,
                                          employeeWiseTasks.length
                                      )}–${Math.min(
                                          currentPage * itemsPerPage,
                                          employeeWiseTasks.length
                                      )} of ${employeeWiseTasks.length} employees`}
                            </span>
                        </div>

                        {itemsPerPage !== "All" && totalPages > 1 && (
                            <div className="flex items-center gap-2">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`h-9 w-9 rounded-xl text-xs font-black transition-all ${
                                            currentPage === pageNum
                                                ? "bg-[var(--accent-primary)] text-white shadow-md shadow-[var(--accent-primary)]/20"
                                                : "border border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* ================= EMPLOYEE TASKS POPUP MODAL ================= */}
            {activeEmployeeGroup && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setSelectedEmployeeKey(null);
                    }}
                >
                    <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl">
                        {/* MODAL HEADER WITH ELEGANT LIGHT SHADE */}
                        <div className="flex items-center justify-between border-b border-[var(--border-color)] bg-gradient-to-r from-teal-500/10 via-cyan-500/[0.06] to-emerald-500/10 dark:from-teal-950/40 dark:via-cyan-950/30 dark:to-emerald-950/40 px-6 py-5">
                            <div className="flex min-w-0 items-center gap-3.5">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[var(--accent-primary)]/20 bg-[var(--accent-primary)]/15 text-sm font-black text-[var(--accent-primary)] shadow-sm">
                                    {(activeEmployeeGroup.employee.name || "E")
                                        .split(/\s+/)
                                        .filter(Boolean)
                                        .slice(0, 2)
                                        .map((part) => part.charAt(0).toUpperCase())
                                        .join("") || "--"}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="truncate text-xl font-black text-[var(--text-primary)]">
                                        {activeEmployeeGroup.employee.name}'s Tasks
                                    </h3>
                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                        <span className="rounded-lg border border-[var(--accent-primary)]/25 bg-[var(--accent-primary)]/10 px-2.5 py-0.5 text-[11px] font-bold text-[var(--accent-primary)]">
                                            {activeEmployeeGroup.tasks.length} Assigned
                                        </span>
                                        <span className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                                            {
                                                activeEmployeeGroup.tasks.filter((t) => t.status === "completed")
                                                    .length
                                            }{" "}
                                            Completed
                                        </span>
                                        <span className="text-xs font-semibold text-[var(--text-secondary)]">
                                            {[
                                                formatRoleLabel(activeEmployeeGroup.employee.role),
                                                activeEmployeeGroup.employee.team,
                                            ]
                                                .filter(Boolean)
                                                .join(" • ")}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setSelectedEmployeeKey(null)}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-secondary)] shadow-sm transition-all hover:bg-[var(--bg-primary)] hover:text-[var(--text-primary)]"
                                aria-label="Close modal"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* MODAL BODY (TASK LIST) */}
                        <div className="flex-1 divide-y divide-[var(--border-color)] overflow-y-auto p-2 sm:p-4">
                            {activeEmployeeGroup.tasks.length === 0 ? (
                                <div className="py-12 text-center text-sm text-[var(--text-secondary)]">
                                    No tasks found for this employee.
                                </div>
                            ) : (
                                activeEmployeeGroup.tasks.map((task) => (
                                    <div
                                        key={task._id}
                                        className="grid gap-4 rounded-2xl p-4 transition-colors hover:bg-[var(--bg-primary)]/70 lg:grid-cols-[minmax(0,1.2fr)_140px_150px_160px_auto] lg:items-center"
                                    >
                                        {/* TITLE & DESCRIPTION */}
                                        <div className="min-w-0 pr-2">
                                            <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                                <h4 className="font-black text-sm text-[var(--text-primary)]">
                                                    {task.title}
                                                </h4>
                                                <span
                                                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase ${priorityStyles(
                                                        task.priority
                                                    )}`}
                                                >
                                                    {task.priority || "medium"}
                                                </span>
                                            </div>
                                            <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-[var(--text-secondary)]">
                                                {task.description}
                                            </p>
                                        </div>

                                        {/* ASSIGNED BY */}
                                        <div className="min-w-0 text-xs text-[var(--text-secondary)]">
                                            <span className="block text-[10px] font-black uppercase text-[var(--text-secondary)]/70">
                                                Assigned by
                                            </span>
                                            <strong className="mt-0.5 block truncate font-bold text-[var(--text-primary)]">
                                                {getTaskAssignerName(task)}
                                            </strong>
                                        </div>

                                        {/* DUE DATE */}
                                        <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
                                            <CalendarDays
                                                size={15}
                                                className="shrink-0 text-[var(--accent-primary)]"
                                            />
                                            <span>{formatTaskDateHeading(getTaskDateKey(task))}</span>
                                        </div>

                                        {/* STATUS SELECTOR */}
                                        <div>
                                            <select
                                                value={task.status || "pending"}
                                                disabled={updatingId === task._id}
                                                onChange={(e) =>
                                                    handleStatusChange(task._id, e.target.value)
                                                }
                                                className={`h-9 w-full rounded-xl border px-3 text-xs font-bold outline-none transition-all focus:ring-2 focus:ring-[var(--accent-primary)]/20 ${statusBadgeStyles(
                                                    task.status
                                                )}`}
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="in-progress">In Progress</option>
                                                <option value="blocked">Blocked</option>
                                                <option value="completed">Completed</option>
                                            </select>
                                        </div>

                                        {/* ACTION BUTTONS */}
                                        <div className="flex items-center gap-2 lg:justify-end">
                                            <button
                                                type="button"
                                                onClick={() => openEdit(task)}
                                                className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--accent-primary)] transition-all hover:bg-[var(--accent-primary)] hover:text-white"
                                                title="Edit task"
                                            >
                                                <Pencil size={15} />
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => openDelete(task)}
                                                className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] text-rose-500 transition-all hover:bg-rose-500 hover:text-white"
                                                title="Delete task"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* MODAL FOOTER */}
                        <div className="flex items-center justify-end border-t border-[var(--border-color)] bg-[var(--bg-primary)]/50 px-6 py-4">
                            <button
                                type="button"
                                onClick={() => setSelectedEmployeeKey(null)}
                                className="h-10 rounded-xl bg-[var(--card-bg)] border border-[var(--border-color)] px-5 text-xs font-bold text-[var(--text-primary)] hover:bg-[var(--bg-primary)] transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= CREATE TASK MODAL ================= */}
            {showCreateForm && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setShowCreateForm(false);
                    }}
                >
                    <form
                        onSubmit={handleCreate}
                        className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 sm:p-8 space-y-6 shadow-2xl"
                    >
                        <button
                            type="button"
                            onClick={() => setShowCreateForm(false)}
                            className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            aria-label="Close form"
                        >
                            <X size={18} />
                        </button>

                        <div className="flex items-center gap-3.5">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]">
                                <Plus size={22} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-[var(--text-primary)]">
                                    Create New Task
                                </h2>
                                <p className="text-xs text-[var(--text-secondary)]">
                                    Assign single or scheduled tasks to employees
                                </p>
                            </div>
                        </div>

                        {/* STEP 1: TEAM SELECTION */}
                        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-4">
                            <div className="mb-3 flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
                                        Step 1
                                    </span>
                                    <label className="block text-sm font-bold text-[var(--text-primary)]">
                                        Select Team
                                    </label>
                                </div>
                                {taskTeam && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTaskTeam("");
                                            setSelectedEmployees([]);
                                            setAssignToEntireTeam(false);
                                        }}
                                        className="text-xs font-bold text-[var(--accent-primary)] hover:underline"
                                    >
                                        Change Team
                                    </button>
                                )}
                            </div>

                            {taskTeam ? (
                                <div className="flex items-center gap-3 rounded-xl border border-[var(--accent-primary)]/30 bg-[var(--accent-primary)]/10 px-4 py-3">
                                    <Users size={18} className="text-[var(--accent-primary)]" />
                                    <span className="text-sm font-black text-[var(--text-primary)]">
                                        {teams.find((t) => t.value === taskTeam)?.name || taskTeam}
                                    </span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                                    {teams.map((team) => (
                                        <button
                                            key={team._id}
                                            type="button"
                                            onClick={() => {
                                                setTaskTeam(team.value);
                                                setSelectedEmployees([]);
                                                setAssignToEntireTeam(false);
                                            }}
                                            className="min-h-12 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3.5 py-2 text-left text-xs font-bold transition-all hover:border-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/5 hover:text-[var(--accent-primary)]"
                                        >
                                            {team.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* STEP 2 & 3: EMPLOYEE SELECTION & TASK DETAILS */}
                        {taskTeam ? (
                            <>
                                {/* ASSIGN TO ENTIRE TEAM */}
                                <label className="flex items-center gap-3 p-4 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] cursor-pointer hover:bg-[var(--bg-primary)]/80 transition-all select-none">
                                    <input
                                        type="checkbox"
                                        checked={assignToEntireTeam}
                                        onChange={(e) => {
                                            setAssignToEntireTeam(e.target.checked);
                                            if (e.target.checked) setSelectedEmployees([]);
                                        }}
                                        className="h-5 w-5 rounded accent-[var(--accent-primary)] cursor-pointer"
                                    />
                                    <div>
                                        <h4 className="font-bold text-sm text-[var(--text-primary)]">
                                            Assign to entire {taskTeam.charAt(0).toUpperCase() + taskTeam.slice(1)} Team
                                        </h4>
                                        <p className="text-xs text-[var(--text-secondary)]">
                                            Task will be distributed to all active team members
                                        </p>
                                    </div>
                                </label>

                                {/* EMPLOYEE CHECKBOX LIST */}
                                {!assignToEntireTeam && (
                                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-4">
                                        <div className="mb-3 flex items-center gap-2">
                                            <Users size={16} className="text-[var(--accent-primary)]" />
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                                                Select Assignees ({selectedEmployees.length} selected)
                                            </h4>
                                        </div>

                                        {filteredEmployeesForTeam.length === 0 ? (
                                            <p className="text-xs text-[var(--text-secondary)] italic p-2 text-center">
                                                No active employees in this team
                                            </p>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-48 overflow-y-auto pr-1">
                                                {filteredEmployeesForTeam.map((emp) => (
                                                    <label
                                                        key={emp._id}
                                                        className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                                                            selectedEmployees.includes(emp._id)
                                                                ? "border-[var(--accent-primary)] bg-[var(--accent-primary)]/10"
                                                                : "border-[var(--border-color)] bg-[var(--card-bg)] hover:border-[var(--accent-primary)]/40"
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedEmployees.includes(emp._id)}
                                                            onChange={() => handleCheckbox(emp._id)}
                                                            className="h-4 w-4 rounded accent-[var(--accent-primary)]"
                                                        />
                                                        <div className="min-w-0">
                                                            <h5 className="truncate text-xs font-bold text-[var(--text-primary)]">
                                                                {emp.name}
                                                            </h5>
                                                            <p className="truncate text-[10px] text-[var(--text-secondary)]">
                                                                {formatRoleLabel(emp.role)}
                                                            </p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* TASK PARAMETERS */}
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                    {/* PRIORITY */}
                                    <div>
                                        <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                            Priority
                                        </label>
                                        <select
                                            value={form.priority}
                                            onChange={(e) => setForm({ ...form, priority: e.target.value })}
                                            className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                        >
                                            <option value="low">Low Priority</option>
                                            <option value="medium">Medium Priority</option>
                                            <option value="high">High Priority</option>
                                        </select>
                                    </div>

                                    {/* ASSIGNMENT MODE */}
                                    <div>
                                        <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                            Schedule Mode
                                        </label>
                                        <select
                                            value={form.assignmentMode}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    assignmentMode: e.target.value,
                                                    weeklyWorkMode:
                                                        e.target.value === "weekly"
                                                            ? form.weeklyWorkMode
                                                            : e.target.value === "range"
                                                            ? "different"
                                                            : "same",
                                                })
                                            }
                                            className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                        >
                                            <option value="single">Single Day</option>
                                            <option value="weekly">One Week (7 Days)</option>
                                            <option value="range">Custom Date Range</option>
                                        </select>
                                    </div>

                                    {/* DATE SELECTION */}
                                    {form.assignmentMode === "single" ? (
                                        <div>
                                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                                Task Date
                                            </label>
                                            <input
                                                type="date"
                                                min={today}
                                                required
                                                value={form.dueDate}
                                                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                            />
                                        </div>
                                    ) : form.assignmentMode === "weekly" ? (
                                        <div>
                                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                min={today}
                                                required
                                                value={form.dueDate}
                                                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                                From - To
                                            </label>
                                            <div className="grid grid-cols-2 gap-2">
                                                <input
                                                    type="date"
                                                    min={today}
                                                    required
                                                    value={form.fromDate}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            fromDate: e.target.value,
                                                            toDate:
                                                                form.toDate && form.toDate < e.target.value
                                                                    ? e.target.value
                                                                    : form.toDate,
                                                        })
                                                    }
                                                    className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 text-xs font-bold outline-none"
                                                />
                                                <input
                                                    type="date"
                                                    min={form.fromDate || today}
                                                    required
                                                    value={form.toDate}
                                                    onChange={(e) => setForm({ ...form, toDate: e.target.value })}
                                                    className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 text-xs font-bold outline-none"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {form.assignmentMode === "weekly" && (
                                    <div>
                                        <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                            Weekly Task Content
                                        </label>
                                        <select
                                            value={form.weeklyWorkMode}
                                            onChange={(e) =>
                                                setForm({ ...form, weeklyWorkMode: e.target.value })
                                            }
                                            className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                        >
                                            <option value="same">Same task title and description every day</option>
                                            <option value="different">Specific task per day</option>
                                        </select>
                                    </div>
                                )}

                                {/* TITLE & DESCRIPTION INPUTS */}
                                {!(
                                    form.assignmentMode === "range" ||
                                    (form.assignmentMode === "weekly" && form.weeklyWorkMode === "different")
                                ) ? (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                                Task Title
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Implement Drone Telemetry Dashboard"
                                                required
                                                value={form.title}
                                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-xs font-medium outline-none focus:border-[var(--accent-primary)]"
                                            />
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                                Task Description
                                            </label>
                                            <textarea
                                                rows={4}
                                                placeholder="Provide detailed instructions and expectations..."
                                                required
                                                value={form.description}
                                                onChange={(e) =>
                                                    setForm({ ...form, description: e.target.value })
                                                }
                                                className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3.5 text-xs font-medium outline-none focus:border-[var(--accent-primary)]"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-4">
                                        <h5 className="mb-3 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                                            Day-by-Day Task Schedule
                                        </h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-1">
                                            {weeklyDates.map((day, index) => (
                                                <div
                                                    key={day.key}
                                                    className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3"
                                                >
                                                    <div className="mb-2 flex items-center justify-between text-xs font-black text-[var(--accent-primary)]">
                                                        <span>{day.label}</span>
                                                        {day.isSunday && (
                                                            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-600 font-bold">
                                                                Sunday
                                                            </span>
                                                        )}
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder="Day task title"
                                                        value={form.weeklyTasks[index]?.title || ""}
                                                        onChange={(e) => {
                                                            const next = [...form.weeklyTasks];
                                                            next[index] = {
                                                                ...(next[index] || {}),
                                                                title: e.target.value,
                                                            };
                                                            setForm({ ...form, weeklyTasks: next });
                                                        }}
                                                        className="mb-2 h-9 w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-medium outline-none"
                                                    />
                                                    <textarea
                                                        rows={2}
                                                        placeholder="Day task description"
                                                        value={form.weeklyTasks[index]?.description || ""}
                                                        onChange={(e) => {
                                                            const next = [...form.weeklyTasks];
                                                            next[index] = {
                                                                ...(next[index] || {}),
                                                                description: e.target.value,
                                                            };
                                                            setForm({ ...form, weeklyTasks: next });
                                                        }}
                                                        className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-2 text-xs font-medium outline-none"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateForm(false)}
                                        className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-5 text-xs font-bold hover:border-[var(--accent-primary)]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="h-11 rounded-xl bg-[var(--accent-primary)] px-6 text-xs font-bold text-white shadow-md shadow-[var(--accent-primary)]/20 hover:bg-[var(--accent-hover)] transition-all"
                                    >
                                        Assign Task
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-[var(--border-color)] p-6 text-center text-xs text-[var(--text-secondary)] bg-[var(--bg-primary)]/30">
                                Please select a team first to proceed with task assignment.
                            </div>
                        )}
                    </form>
                </div>
            )}

            {/* ================= EDIT TASK MODAL ================= */}
            {editingTask && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) closeEdit();
                    }}
                >
                    <form
                        onSubmit={handleEditSave}
                        className="w-full max-w-xl rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 sm:p-7 shadow-2xl space-y-4"
                    >
                        <div className="flex items-center justify-between pb-2 border-b border-[var(--border-color)]">
                            <div>
                                <h3 className="text-xl font-black text-[var(--text-primary)]">
                                    Edit Task
                                </h3>
                                <p className="text-xs text-[var(--text-secondary)]">
                                    Update assignment details for this specific task
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeEdit}
                                className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div>
                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                Title
                            </label>
                            <input
                                type="text"
                                value={editForm.title}
                                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                placeholder="Task title"
                                required
                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-medium outline-none focus:border-[var(--accent-primary)]"
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div>
                                <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                    Priority
                                </label>
                                <select
                                    value={editForm.priority}
                                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                                    className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                    Status
                                </label>
                                <select
                                    value={editForm.status}
                                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                    className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="blocked">Blocked</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                    Due Date
                                </label>
                                <input
                                    type="date"
                                    value={editForm.dueDate}
                                    onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                                    required
                                    className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-xs font-bold text-[var(--text-secondary)]">
                                Description
                            </label>
                            <textarea
                                rows={4}
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                placeholder="Task description..."
                                className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3 text-xs font-medium outline-none focus:border-[var(--accent-primary)]"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={closeEdit}
                                className="h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-xs font-bold hover:border-[var(--accent-primary)]"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={savingEdit}
                                className="h-10 rounded-xl bg-[var(--accent-primary)] px-5 text-xs font-bold text-white shadow-md shadow-[var(--accent-primary)]/20 hover:bg-[var(--accent-hover)] transition-all disabled:opacity-60"
                            >
                                {savingEdit ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ================= DELETE CONFIRMATION ================= */}
            <ConfirmModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDelete}
                title="Delete Task"
                message={`Are you sure you want to delete task "${selectedTask?.title}"? This action cannot be undone.`}
                confirmText="Delete Task"
                cancelText="Cancel"
                loading={deleting}
                variant="danger"
            />
        </div>
    );
};

export default Tasks;
